/**
 * Shared AuthProvider for all Brent Symphony Orchestra portals.
 *
 * All three portals (member, admin, concert-archive) now use Cognito Managed Login
 * exclusively. This single provider replaces the three separate AuthContext
 * implementations with a unified OAuth-only flow:
 *
 *   1. login()  → redirect to Managed Login URL
 *   2. Managed Login handles email/password, Google, password reset, MFA, force-change
 *   3. Callback → exchangeCodeForTokens() → storeOAuthTokensInCognitoFormat()
 *   4. logout() → clearOAuthTokens() + redirect to Cognito logout URL
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { CognitoUserPool } from 'amazon-cognito-identity-js'
import { validateCognitoConfig, type CognitoConfig } from './cognito-config'
import {
  generateOAuthUrl,
  generateLogoutUrl,
  exchangeCodeForTokens,
  storeOAuthTokensInCognitoFormat,
  clearOAuthTokens,
} from './oauth-utils'
import {
  getCurrentUser as getSharedCurrentUser,
  getAccessToken as getSharedAccessToken,
} from './session'
import type { User } from './types'

// ── Configuration ─────────────────────────────────────────────────────────────

export interface AuthProviderConfig {
  userPoolId: string
  clientId: string
  region: string
  domain: string
  redirectUri: string
  logoutUri: string
  /** Force a specific identity provider (e.g. 'Google' for admin portal) */
  identityProvider?: string
  /** Custom prefix for clearing legacy token formats during migration */
  legacyTokenPrefix?: string
}

// ── Context type ──────────────────────────────────────────────────────────────

export interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Whether the user belongs to a specific Cognito group */
  hasGroup: (group: string) => boolean
  /** Redirect to Cognito Managed Login */
  login: () => Promise<void>
  /** Handle the OAuth callback (exchange code, store tokens, rehydrate user) */
  handleCallback: () => Promise<void>
  /** Clear tokens and redirect to Cognito logout */
  logout: () => void
  /** Get the current access token JWT string */
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// ── useAuth hook ──────────────────────────────────────────────────────────────

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// ── Provider component ────────────────────────────────────────────────────────

interface AuthProviderProps {
  config: AuthProviderConfig
  children: ReactNode
}

export function AuthProvider({ config, children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Validate and create Cognito objects once
  const cognitoConfig: CognitoConfig = validateCognitoConfig({
    userPoolId: config.userPoolId,
    clientId: config.clientId,
    region: config.region,
    domain: config.domain,
  })

  const userPool = new CognitoUserPool({
    UserPoolId: cognitoConfig.userPoolId,
    ClientId: cognitoConfig.clientId,
  })

  // ── Session rehydration ───────────────────────────────────────────────────

  const getCurrentUser = useCallback(
    () => getSharedCurrentUser(userPool),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cognitoConfig.userPoolId, cognitoConfig.clientId],
  )

  const getAccessToken = useCallback(
    () => getSharedAccessToken(userPool),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cognitoConfig.userPoolId, cognitoConfig.clientId],
  )

  // ── Login ─────────────────────────────────────────────────────────────────

  const login = useCallback(async () => {
    const authUrl = await generateOAuthUrl({
      cognitoConfig,
      redirectUri: config.redirectUri,
      identityProvider: config.identityProvider,
      scopes: ['openid', 'email', 'profile'],
    })

    window.location.href = authUrl
  }, [cognitoConfig, config.redirectUri, config.identityProvider])

  // ── Callback ──────────────────────────────────────────────────────────────

  const handleCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state') || undefined
    const error = params.get('error')
    const errorDescription = params.get('error_description')

    if (error) {
      throw new Error(`OAuth error: ${error} - ${errorDescription}`)
    }

    if (!code) {
      throw new Error('No authorization code found in callback URL')
    }

    // Exchange code for tokens (validates state + PKCE)
    const tokens = await exchangeCodeForTokens(
      code,
      { cognitoConfig, redirectUri: config.redirectUri },
      state,
    )

    // Store tokens in Cognito SDK format
    storeOAuthTokensInCognitoFormat(tokens, cognitoConfig.clientId)

    // Rehydrate user from stored tokens
    const currentUser = await getCurrentUser()
    setUser(currentUser)
  }, [cognitoConfig, config.redirectUri, getCurrentUser])

  // ── Logout ────────────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    // Clear tokens from localStorage
    clearOAuthTokens(cognitoConfig.clientId, config.legacyTokenPrefix)

    // Sign out from Cognito user pool (clears SDK session)
    const cognitoUser = userPool.getCurrentUser()
    if (cognitoUser) {
      cognitoUser.signOut()
    }

    setUser(null)

    // Redirect to Cognito logout URL to invalidate server-side session
    const logoutUrl = generateLogoutUrl({
      cognitoConfig,
      redirectUri: config.redirectUri,
      logoutUri: config.logoutUri,
    })

    window.location.href = logoutUrl
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoConfig, config.redirectUri, config.logoutUri, config.legacyTokenPrefix])

  // ── Group check ───────────────────────────────────────────────────────────

  const hasGroup = useCallback(
    (group: string): boolean => {
      if (!user) return false
      return user.groups.includes(group)
    },
    [user],
  )

  // ── Mount: rehydrate session ──────────────────────────────────────────────

  useEffect(() => {
    getCurrentUser()
      .then((u) => {
        setUser(u)
        setIsLoading(false)
      })
      .catch(() => {
        setUser(null)
        setIsLoading(false)
      })
  }, [getCurrentUser])

  // ── Context value ─────────────────────────────────────────────────────────

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasGroup,
    login,
    handleCallback,
    logout,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
